import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import ScoreBadge from '../components/ScoreBadge';
import { Calendar as CalendarIcon } from 'lucide-react';
import {
  Upload, CheckCircle, AlertCircle, Loader2, FileSpreadsheet,
  Users, ArrowRight, ArrowLeft, X, ChevronRight
} from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { BACKEND_URL } from '../lib/config';

const STEPS = ['Select Location & Cycle', 'Upload Evaluation Sheet', 'Review & Save'];

export default function NewEvaluation() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [locations, setLocations] = useState([]);
  const [batches, setBatches] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [form, setForm] = useState({ location_id: '', batch_id: '', cycle_id: '', evaluation_date: '' });
  const [file, setFile] = useState(null);
  const [sheetUrl, setSheetUrl] = useState('');
  const [inputMode, setInputMode] = useState('file');
  const [dragActive, setDragActive] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [parseError, setParseError] = useState('');
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [sheetsLoading, setSheetsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const fileRef = useRef();

  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/locations`),
      axios.get(`${BACKEND_URL}/api/batches`),
    ]).then(([l, b]) => { setLocations(l.data); setBatches(b.data); }).catch(console.error);
  }, []);

  // Fetch cycles when batch changes
  useEffect(() => {
    if (form.batch_id) {
      axios.get(`${BACKEND_URL}/api/batches/${form.batch_id}/cycles`)
        .then(res => setCycles(res.data.filter(c => c.is_active)))
        .catch(() => setCycles([]));
    } else {
      setCycles([]);
      setForm(p => ({ ...p, cycle_id: '' }));
    }
  }, [form.batch_id]);

  const filteredBatches = form.location_id
    ? (() => {
        const selectedLoc = locations.find(l => l.id === form.location_id);
        return batches.filter(b => (selectedLoc?.batch_ids || []).includes(b.id));
      })()
    : batches;

  // Workbooks routinely mix attendance and evaluation tabs, so only real xlsx
  // uploads and Google Sheet links (whose export always keeps every tab) go
  // through the "load sheets, then pick one" flow — .xls/.csv parse directly,
  // same as before.
  const needsSheetPicker = inputMode === 'sheet' || (inputMode === 'file' && !!file && file.name.toLowerCase().endsWith('.xlsx'));

  const resetSheetPicker = () => { setSheetNames([]); setSelectedSheet(''); };

  const handleDrag = (e) => { e.preventDefault(); setDragActive(e.type === 'dragenter' || e.type === 'dragover'); };
  const handleDrop = (e) => {
    e.preventDefault(); setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) { setFile(f); resetSheetPicker(); setParseError(''); }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); resetSheetPicker(); setParseError(''); }
  };

  const handleSheetUrlChange = (e) => { setSheetUrl(e.target.value); resetSheetPicker(); };

  const handleLoadSheets = async () => {
    setSheetsLoading(true); setParseError(''); resetSheetPicker();
    try {
      const fd = new FormData();
      const endpoint = inputMode === 'file' ? 'list-sheets' : 'list-sheets-google';
      if (inputMode === 'file') fd.append('file', file);
      else fd.append('sheet_url', sheetUrl.trim());
      const res = await axios.post(`${BACKEND_URL}/api/evaluations/${endpoint}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const sheets = res.data.sheets || [];
      setSheetNames(sheets);
      const evalMatches = sheets.filter(s => /eval/i.test(s));
      if (sheets.length === 1) setSelectedSheet(sheets[0]);
      else if (evalMatches.length === 1) setSelectedSheet(evalMatches[0]);
    } catch (e) {
      setParseError(e?.response?.data?.detail || 'Failed to read sheet names from the file');
    } finally { setSheetsLoading(false); }
  };

  const handleParseExcel = async () => {
    if (inputMode === 'file' && !file) { setParseError('Please select a file'); return; }
    if (inputMode === 'sheet' && !sheetUrl.trim()) { setParseError('Please paste a Google Sheets link'); return; }
    if (needsSheetPicker && !selectedSheet) { setParseError('Please select which sheet to parse'); return; }
    if (!form.batch_id || !form.location_id || !form.cycle_id) { setParseError('Please fill all fields'); return; }
    setParsing(true); setParseError(''); setParseResult(null);
    try {
      const fd = new FormData();
      if (inputMode === 'file') {
        fd.append('file', file);
      } else {
        fd.append('sheet_url', sheetUrl.trim());
      }
      if (selectedSheet) fd.append('sheet_name', selectedSheet);
      fd.append('batch_id', form.batch_id);
      fd.append('location_id', form.location_id);
      fd.append('cycle_id', form.cycle_id);
      fd.append('evaluation_date', form.evaluation_date || new Date().toISOString().slice(0, 10));
      const endpoint = inputMode === 'file' ? 'parse-excel' : 'parse-google-sheet';
      const res = await axios.post(`${BACKEND_URL}/api/evaluations/${endpoint}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setParseResult(res.data);
      setStep(2);
    } catch (e) {
      setParseError(e?.response?.data?.detail || 'Failed to parse evaluation sheet');
    } finally { setParsing(false); }
  };

  const sheetsReady = !needsSheetPicker || sheetNames.length > 0;
  const canLoadSheets = inputMode === 'file' ? !!file : !!sheetUrl.trim();

  const handleSave = async () => {
    if (!parseResult) return;
    setSaving(true); setSaveError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/evaluations`, {
        batch_id: parseResult.batch_id,
        location_id: parseResult.location_id,
        cycle_id: parseResult.cycle_id || form.cycle_id,
        evaluation_date: parseResult.evaluation_date,
        players_data: parseResult.players,
      });
      navigate(`/coach/evaluations/${res.data.id}`);
    } catch (e) {
      setSaveError(e?.response?.data?.detail || 'Failed to save evaluation');
    } finally { setSaving(false); }
  };

  const canStep1 = form.location_id && form.batch_id && form.cycle_id;

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold">New Evaluation</h1>
          <p className="text-sm text-muted-foreground">Upload an Excel evaluation sheet</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 ${ i <= step ? 'text-primary' : 'text-muted-foreground'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                  i < step ? 'bg-primary border-primary text-[#0D1117]' :
                  i === step ? 'border-primary text-primary' : 'border-border/60'
                }`}>
                  {i < step ? <CheckCircle size={14} /> : i + 1}
                </div>
                <span className="text-xs font-medium hidden sm:block">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${ i < step ? 'bg-primary' : 'bg-border/60'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 0: Select Batch */}
        {step === 0 && (
          <Card className="bg-[#111827] border-border/60">
            <CardHeader><CardTitle className="text-base">Select Location, Batch & Cycle</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Location</Label>
                <Select value={form.location_id} onValueChange={v => setForm(p => ({...p, location_id: v, batch_id: ''})) }>
                  <SelectTrigger data-testid="location-select" className="mt-1 bg-secondary/50 border-border/60">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-border/60">
                    {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Batch</Label>
                <Select value={form.batch_id} onValueChange={v => setForm(p => ({...p, batch_id: v})) }>
                  <SelectTrigger data-testid="batch-select" className="mt-1 bg-secondary/50 border-border/60">
                    <SelectValue placeholder="Select batch" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-border/60">
                    {filteredBatches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Evaluation Cycle</Label>
                <Select value={form.cycle_id} onValueChange={v => setForm(p => ({...p, cycle_id: v}))}>
                  <SelectTrigger data-testid="cycle-select" className="mt-1 bg-secondary/50 border-border/60">
                    <SelectValue placeholder={form.batch_id ? "Select evaluation cycle" : "Select a batch first"} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-border/60">
                    {cycles.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    {cycles.length === 0 && form.batch_id && (
                      <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                        No evaluation cycles for this batch yet.
                        <br />Go to Admin → Evaluation Cycles to create one.
                      </div>
                    )}
                  </SelectContent>
                </Select>
                {form.cycle_id && (() => {
                  const selectedCycle = cycles.find(c => c.id === form.cycle_id);
                  if (selectedCycle?.template_id) {
                    return <p className="text-xs text-green-400 mt-1">Template assigned to this cycle</p>;
                  }
                  return <p className="text-xs text-amber-400 mt-1">No template assigned — contact admin</p>;
                })()}
              </div>
              <div>
                <Label>Evaluation Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal mt-1 bg-secondary/50 border-border/60",
                        !form.evaluation_date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.evaluation_date
                        ? format(new Date(form.evaluation_date), "dd/MM/yyyy")
                        : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.evaluation_date ? new Date(form.evaluation_date) : undefined}
                      onSelect={(date) =>
                        setForm(p => ({
                          ...p,
                          evaluation_date: date ? format(date, "yyyy-MM-dd") : "",
                        }))
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <Button
                data-testid="next-step-btn"
                className="w-full bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold"
                disabled={!canStep1}
                onClick={() => setStep(1)}
              >
                Next <ArrowRight size={16} className="ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Upload Excel */}
        {step === 1 && (
          <Card className="bg-[#111827] border-border/60">
            <CardHeader><CardTitle className="text-base">Add Evaluation Sheet</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Button type="button" variant={inputMode === 'file' ? 'default' : 'outline'} className="flex-1" onClick={() => { setInputMode('file'); setParseError(''); resetSheetPicker(); }}>
                  Upload file
                </Button>
                <Button type="button" variant={inputMode === 'sheet' ? 'default' : 'outline'} className="flex-1" onClick={() => { setInputMode('sheet'); setParseError(''); resetSheetPicker(); }}>
                  Google Sheet link
                </Button>
              </div>
              {inputMode === 'sheet' ? (
                <div className="space-y-2">
                  <Label htmlFor="google-sheet-url">Google Sheet link</Label>
                  <Input
                    id="google-sheet-url"
                    data-testid="google-sheet-url"
                    type="url"
                    placeholder="Paste your Google Sheet link"
                    value={sheetUrl}
                    onChange={handleSheetUrlChange}
                    className="bg-secondary/50 border-border/60"
                  />
                  <p className="text-xs text-muted-foreground">
                    In Google Sheets, choose Share → General access → Anyone with the link → Viewer, then paste the link here.
                  </p>
                </div>
              ) : (
              <div
                data-testid="dropzone"
                onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  dragActive ? 'dropzone-active' : 'border-border/60 hover:border-primary/40'
                }`}
              >
                {file ? (
                  <div>
                    <FileSpreadsheet size={32} className="mx-auto text-green-400 mb-2" />
                    <p className="text-sm font-medium">{file.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                    <Button variant="ghost" size="sm" className="mt-2 text-muted-foreground" onClick={e => { e.stopPropagation(); setFile(null); resetSheetPicker(); }}>
                      <X size={12} className="mr-1" /> Remove
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Upload size={32} className="mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm font-medium">Drop your Excel file here</p>
                    <p className="text-xs text-muted-foreground mt-1">or click to browse (.xlsx, .xls, .csv)</p>
                  </div>
                )}
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileChange} />
              </div>
              )}

              {needsSheetPicker && sheetNames.length > 0 && (
                <div>
                  <Label>Evaluation Sheet / Tab</Label>
                  <Select value={selectedSheet} onValueChange={setSelectedSheet}>
                    <SelectTrigger data-testid="sheet-select" className="mt-1 bg-secondary/50 border-border/60">
                      <SelectValue placeholder="Select the sheet with player evaluations" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111827] border-border/60">
                      {sheetNames.map(name => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    This file has multiple tabs (e.g. monthly attendance sheets) — pick the one with player scores and comments.
                  </p>
                </div>
              )}

              {parseError && (
                <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
                  <AlertDescription>{parseError}</AlertDescription>
                </Alert>
              )}

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setStep(0)}><ArrowLeft size={14} className="mr-1" /> Back</Button>
                <Button
                  data-testid="parse-excel-btn"
                  className="flex-1 bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold"
                  disabled={!canLoadSheets || parsing || sheetsLoading || (sheetsReady && needsSheetPicker && !selectedSheet)}
                  onClick={sheetsReady ? handleParseExcel : handleLoadSheets}
                >
                  {sheetsLoading
                    ? <><Loader2 size={14} className="animate-spin mr-1" /> Reading sheets...</>
                    : !sheetsReady
                      ? <>Load Sheets <ArrowRight size={14} className="ml-1" /></>
                      : parsing
                        ? <><Loader2 size={14} className="animate-spin mr-1" /> Parsing...</>
                        : <>{inputMode === 'file' ? 'Parse Excel' : 'Parse Google Sheet'} <ArrowRight size={14} className="ml-1" /></>}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Review */}
        {step === 2 && parseResult && (
          <Card className="bg-[#111827] border-border/60">
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                Review Players
                <Badge variant="outline" className="border-primary/30 text-primary">
                  <Users size={12} className="mr-1" />{parseResult.player_count} players
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-3 bg-secondary/30 rounded-lg">
                <div><p className="text-xs text-muted-foreground">Cycle</p><p className="text-sm font-medium">{parseResult.cycle_name}</p></div>
                <div><p className="text-xs text-muted-foreground">Skills Found</p><p className="text-sm font-medium">{parseResult.skill_columns?.length}</p></div>
                <div><p className="text-xs text-muted-foreground">Players</p><p className="text-sm font-medium">{parseResult.player_count}</p></div>
                <div><p className="text-xs text-muted-foreground">Comment fields</p><p className="text-sm font-medium">{parseResult.comment_columns?.length || 0}</p></div>
              </div>

              <div className="max-h-80 overflow-y-auto space-y-1.5">
                {parseResult.players.map((p, i) => {
                  const scores = Object.values(p.scores || {}).filter(v => v !== null);
                  const avg = scores.length ? (scores.reduce((a, b) => a + Number(b), 0) / scores.length).toFixed(1) : null;
                  return (
                    <div key={p.player_id || `player-${p.sno}-${i}`} className="flex items-center justify-between p-2.5 rounded-lg bg-secondary/30">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground w-5">{p.sno}</span>
                        <span className="text-sm font-medium">{p.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{scores.length} scores</span>
                        {avg && <ScoreBadge score={avg} />}
                      </div>
                    </div>
                  );
                })}
              </div>

              {saveError && (
                <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
                  <AlertDescription>{saveError}</AlertDescription>
                </Alert>
              )}

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { setStep(1); setParseResult(null); }}><ArrowLeft size={14} className="mr-1" /> Back</Button>
                <Button
                  data-testid="save-evaluation-btn"
                  className="flex-1 bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold"
                  disabled={saving}
                  onClick={handleSave}
                >
                  {saving ? <><Loader2 size={14} className="animate-spin mr-1" /> Saving...</> : <>Save & Continue <ChevronRight size={14} className="ml-1" /></>}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
